import React from 'react';
import {
  Package,
  ClipboardList,
  AlertTriangle,
  Zap,
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
} from 'lucide-react';

const iconMap: Record<string, React.ElementType> = {
  Package,
  ClipboardList,
  AlertTriangle,
  Zap,
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
};

interface KpiCardProps {
  label: string;
  value: string | number;
  delta?: string;
  deltaPositive?: boolean;
  iconName: string;
  iconColor?: string;
  alert?: boolean;
  alertLabel?: string;
  className?: string;
}

export default function KpiCard({
  label,
  value,
  delta,
  deltaPositive,
  iconName,
  iconColor = '#0D3B2E',
  alert = false,
  alertLabel,
  className = '',
}: KpiCardProps) {
  const IconComponent = iconMap[iconName] || Package;

  return (
    <div
      className={`bg-surface-card rounded-xl p-5 border border-surface-border shadow-card relative overflow-hidden transition-shadow duration-200 hover:shadow-card-md ${
        alert ? 'border-[color:var(--status-red)]/30' : ''
      } ${className}`}
    >
      {alert && (
        <div className="absolute top-0 right-0 bg-[color:var(--status-red)] text-white text-[10px] font-semibold px-2 py-0.5 rounded-bl-lg">
          {alertLabel || 'ALERT'}
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p
            className="text-xs font-medium uppercase tracking-wide mb-3"
            style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}
          >
            {label}
          </p>
          <p
            className="text-3xl font-bold tabular-nums tracking-tight text-text-main"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {value}
          </p>
          {delta && (
            <p
              className={`text-xs mt-2 font-medium ${
                deltaPositive ? 'text-status-green' : 'text-status-red'
              }`}
            >
              {deltaPositive ? '↑ ' : '↓ '}
              {delta}
            </p>
          )}
        </div>

        <div
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ml-3"
          style={{ backgroundColor: `${iconColor}18` }}
        >
          <IconComponent
            size={20}
            style={{ color: iconColor }}
            strokeWidth={1.8}
          />
        </div>
      </div>
    </div>
  );
}